import type { VariantProps } from 'class-variance-authority';

import type { RoomOptionModel } from '@elara/client-core/models/RoomOptionModel';
import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import type { buttonVariants } from '#ui/button-variants.js';
import type { BookingChoice } from './BookingChooserDialog.js';

import { useState } from 'react';

import { resolveRoomCheckoutItems } from '@elara/client-core/lib/room-checkout';
import { isAtStatedRoomLimit, roomCountPickerState } from '@elara/client-core/lib/room-count-picker';
import {
  bookingDisplayStatusLabel,
  bookingStatusFlagsOf,
  resolveBookingDisplayStatus,
} from '@elara/domain/booking-status';

import { bookingStatusLabelColor } from '#lib/booking-status.js';
import { useItineraryState, useNavigate, useTripsState } from '#states/hooks.js';

import { BookingChooserDialog } from './BookingChooserDialog.js';
import { RoomCountPicker } from './RoomCountPicker.js';

/**
 * The room count picker for one rate of a room, wired to the trip: plus and "Add room" add one room
 * (the server checks the hotel has it), minus silently removes the newest added room, Book goes to
 * checkout for the added rooms, and Booked opens the booking — or a chooser when the
 * rate is booked more than once.
 */
export function RoomCountControl({
  room,
  title,
  metadata,
  identityMeta,
  size,
  className,
}: {
  room: RoomOptionModel;
  title: string;
  /** The selection a new item carries: productCode + occupancy. */
  metadata: Record<string, unknown>;
  /** The identity-narrowed metadata rooms of this rate are counted by. */
  identityMeta: Record<string, unknown> | undefined;
  size?: VariantProps<typeof buttonVariants>['size'];
  className?: string;
}) {
  const itineraryState = useItineraryState();
  const tripsState = useTripsState();
  const { navigate } = useNavigate();
  const [isBooking, setIsBooking] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);

  const roomId = room.id;
  const rateIdentity = metadata as { productCode?: string; occupancy?: Occupancy };
  const isAvailable = rateIdentity.productCode
    ? room.isRateAvailableByIdentity({ productCode: rateIdentity.productCode, occupancy: rateIdentity.occupancy })
    : room.isAvailable;

  const items = roomId != null ? itineraryState.getAllItineraryItemsByReference('room', roomId, identityMeta) : [];
  // Already holding every room the hotel says it has (the guide's "N rooms left", or the limit it set by
  // refusing one): every rate of the room stops adding.
  const atStatedLimit =
    roomId != null &&
    isAtStatedRoomLimit(room, rateIdentity, itineraryState.getAllItineraryItemsByReference('room', roomId));
  const state = roomCountPickerState(items, { isAvailable: isAvailable && !atStatedLimit });
  const step = roomId != null ? itineraryState.roomCountStep(roomId, identityMeta) : undefined;
  const busy = step != null;
  const tripId = tripsState.current?.id;

  if (roomId == null) return null;

  const bookedItems = state.kind === 'booked' || state.kind === 'bookedAndAdded' ? state.bookedItems : [];
  const choices: BookingChoice[] = bookedItems.map((item) => {
    const status = resolveBookingDisplayStatus(bookingStatusFlagsOf(item));
    return {
      id: item.id,
      title: item.displayTitle,
      statusLabel: bookingDisplayStatusLabel(status),
      statusClassName: bookingStatusLabelColor(status),
      confirmationNumber: item.booking?.confirmationNumber ?? undefined,
    };
  });

  const increment = () => void itineraryState.incrementRoom({ roomId, metadata, identityMeta });

  const book = async () => {
    if (!tripId || state.kind === 'none' || state.kind === 'booked') return;
    setIsBooking(true);
    try {
      // Only the added rooms go to checkout; booked ones already have their booking.
      const itemIds = await resolveRoomCheckoutItems({
        itineraryState,
        roomId,
        title,
        metadata,
        identityMeta,
        count: state.added,
      });
      if (itemIds.length > 0) navigate(`/checkout/hotel/${tripId}?items=${itemIds.join(',')}`);
    } finally {
      setIsBooking(false);
    }
  };

  const viewBooked = () => {
    const [only, ...rest] = bookedItems;
    if (!only) return;
    if (rest.length === 0) itineraryState.openViewBookingDialog(only);
    else setChooserOpen(true);
  };

  return (
    <>
      <RoomCountPicker
        state={state}
        busy={busy || isBooking}
        step={step}
        booking={isBooking}
        disabledReason={tripId ? undefined : 'Choose a trip to add rooms to it.'}
        soldOut={!isAvailable}
        onAdd={increment}
        onIncrement={increment}
        onDecrement={() => void itineraryState.decrementRoom({ roomId, identityMeta })}
        onBook={() => void book()}
        onViewBooked={viewBooked}
        size={size}
        className={className}
      />
      {choices.length > 1 && (
        <BookingChooserDialog
          open={chooserOpen}
          onOpenChange={setChooserOpen}
          bookings={choices}
          onSelect={(id) => {
            const item = bookedItems.find((i) => i.id === id);
            setChooserOpen(false);
            if (item) itineraryState.openViewBookingDialog(item);
          }}
        />
      )}
    </>
  );
}
